import { Injectable } from '@angular/core';
import { environment } from '@env/environment';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private logLevelOrder: LogLevel[] = ['debug', 'info', 'warn', 'error'];
  private maxLevelLength = Math.max(
    ...this.logLevelOrder.map((level) => level.length),
  );

  constructor() {}

  private shouldLog(level: LogLevel): boolean {
    const currentLevelIndex = this.logLevelOrder.indexOf(environment.logLevel);
    const messageLevelIndex = this.logLevelOrder.indexOf(level);
    return messageLevelIndex >= currentLevelIndex;
  }

  private formatMessage(level: LogLevel, message: any): string {
    const paddedLevel = level.toUpperCase().padEnd(this.maxLevelLength, ' ');
    const messagePrefix = `[${paddedLevel}]`;

    // Check if the message is an object and use console.debug to allow for expanding in the console
    if (typeof message === 'object') {
      console.debug(messagePrefix, message);
      return `${messagePrefix} See details in the console.`;
    } else {
      return `${messagePrefix} ${message}`;
    }
  }

  log(level: LogLevel, message: string, ...optionalParams: any[]): void {
    if (this.shouldLog(level)) {
      const formattedMessage = this.formatMessage(level, message);
      switch (level) {
        case 'debug':
          console.log(formattedMessage, ...optionalParams);
          break;
        case 'info':
          console.info(formattedMessage, ...optionalParams);
          break;
        case 'warn':
          console.warn(formattedMessage, ...optionalParams);
          break;
        case 'error':
          console.error(formattedMessage, ...optionalParams);
          break;
      }
    }
  }

  debug(message: any, ...optionalParams: any[]): void {
    this.log('debug', message, ...optionalParams);
  }

  info(message: string, ...optionalParams: any[]): void {
    this.log('info', message, ...optionalParams);
  }

  warn(message: string, ...optionalParams: any[]): void {
    this.log('warn', message, ...optionalParams);
  }

  error(message: string, ...optionalParams: any[]): void {
    this.log('error', message, ...optionalParams);
  }

  printObject(obj) {
    const cache = new WeakSet();

    const result = JSON.stringify(
      obj,
      (key, value) => {
        if (typeof value === 'function') {
          return `[Function: ${value.name || 'anonymous'}]`;
        }
        if (typeof value === 'object' && value !== null) {
          if (cache.has(value)) {
            return '[Circular]';
          }
          cache.add(value);
        }
        return value;
      },
      2, // pretty print with 2 spaces
    );

    console.log(result);
  }
}
